import type { TextContentModifierValue } from "schemas/generated/fileModifiers";

import React, { useEffect, useRef } from "react";

import { useIframeMessaging } from "@agent-ui/features/modifiers/hooks/useIframeMessaging";
import {
  useSelectedElementContent,
  useSemanticElement,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
} from "lucide-react";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

interface ContentModifierProps {
  onChange: (modifiers: TextContentModifierValue[]) => void;
}

/**
 * Parse text formatting from className string (e.g. Tailwind classes).
 */
function parseFormattingFromClassName(className: string): {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
} {
  return {
    bold: className.includes("font-bold"),
    italic: className.includes("italic"),
    underline: className.includes("underline"),
    strikethrough: className.includes("line-through"),
  };
}

const ContentModifierComponent: React.FC<ContentModifierProps> = ({
  onChange,
}) => {
  const unifiedContent = useSelectedElementContent();
  const semanticElement = useSemanticElement();
  const { sendMessage } = useIframeMessaging();

  // NOTE (Kurt, 2025-12-11): Prevents feedback loops when programmatically updating TipTap content
  const isUpdatingRef = useRef(false);
  // NOTE (Kurt, 2025-12-11): Tracks the current element to detect element switches
  const lastElementIdRef = useRef<string | null>(null);

  const currentValue = React.useMemo(() => {
    if (unifiedContent) {
      return {
        content: unifiedContent.text,
        className: unifiedContent.className,
      };
    }
    return null;
  }, [unifiedContent]);

  // NOTE (Kurt, 2025-12-11): Debounce server requests to avoid flooding the server
  // during rapid typing. The user sees immediate feedback in TipTap, while the
  // iframe preview and server sync happen after the debounce period.
  const debouncedOnChange = useDebouncedCallback(
    (modifiers: TextContentModifierValue[]) => {
      onChange(modifiers);
    },
    300,
  );

  const handleContentChange = (newContent: string, newClassName?: string) => {
    // NOTE (Derek, 2026-02-05): Send instant preview update FIRST (no debounce)
    // This provides immediate visual feedback in the iframe while the user types
    if (semanticElement?.selector) {
      sendMessage({
        type: "UPDATE_TEXT_PREVIEW",
        payload: {
          selector: semanticElement.selector,
          text: newContent,
        },
      });
    }

    if (isUpdatingRef.current) {
      return;
    }

    // NOTE (Derek, 2026-02-05): Debounced persistence (existing behavior)
    debouncedOnChange([
      {
        type: "content" as const,
        value: newContent,
        className: newClassName ?? "",
      },
    ]);
  };

  /**
   * Apply formatting to selected text, or all text if nothing is selected
   */
  const applyFormatting = (formatCommand: () => any) => {
    if (!editor) {
      return;
    }

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      formatCommand();
    } else {
      editor.chain().focus().selectAll().run();
      formatCommand();
    }
  };

  /**
   * Extract content and formatting from TipTap editor
   */
  const extractContentAndFormatting = (editor: any) => {
    const textContent = editor.getText();
    const formattingClasses: string[] = [];

    [
      { type: "bold", className: "font-bold" },
      { type: "italic", className: "italic" },
      { type: "underline", className: "underline" },
      { type: "strike", className: "line-through" },
    ].forEach(({ type, className }) => {
      if (editor.isActive(type)) {
        formattingClasses.push(className);
      }
    });

    const className = formattingClasses.join(" ");
    return { textContent, className };
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
    ],
    content: currentValue?.content ?? "",
    onUpdate: ({ editor }) => {
      if (isUpdatingRef.current) {
        return;
      }
      const { textContent, className } = extractContentAndFormatting(editor);
      handleContentChange(textContent, className);
    },
    editorProps: {
      attributes: {
        class: "content-modifier-editor text-xs break-words",
      },
    },
  });

  // NOTE (Kurt, 2025-12-11): Syncs TipTap editor content with the currently selected element.
  // Handles element switching by canceling pending debounced requests and forcing content refresh.
  // Avoids overwriting user input by skipping updates while the editor is focused (unless
  // the element changed, in which case we must show the new element's content).
  useEffect(() => {
    if (!editor || !currentValue) {
      return;
    }

    const newElementId = semanticElement?.selector ?? null;
    const elementChanged = newElementId !== lastElementIdRef.current;

    if (elementChanged) {
      debouncedOnChange.cancel();
      lastElementIdRef.current = newElementId;
    }

    // Don't overwrite TipTap while user is typing,
    // UNLESS we switched to a different element
    if (editor.isFocused && !elementChanged) {
      return;
    }

    const newContent = currentValue.content ?? "";
    const newClassName = currentValue.className ?? "";
    const currentEditorContent = editor.getText();

    if (currentEditorContent !== newContent || elementChanged) {
      isUpdatingRef.current = true;

      editor.commands.setContent(newContent);

      const existingFormatting = parseFormattingFromClassName(newClassName);

      // Clear all formatting first
      editor
        .chain()
        .selectAll()
        .unsetBold()
        .unsetItalic()
        .unsetUnderline()
        .unsetStrike()
        .run();

      // Apply existing formatting
      const chain = editor.chain().selectAll();

      if (existingFormatting.bold) {
        chain.setBold();
      }
      if (existingFormatting.italic) {
        chain.setItalic();
      }
      if (existingFormatting.underline) {
        chain.setUnderline();
      }
      if (existingFormatting.strikethrough) {
        chain.setStrike();
      }

      chain.run();

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [currentValue, editor, semanticElement, debouncedOnChange]);

  if (!editor) {
    return null;
  }

  const formattingToolbar = (
    <div className="flex gap-1 px-2 py-1 bg-surface border-b border-border items-center">
      <Button
        icon
        variant="tertiary"
        size="sm"
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleBold().run();
          });
        }}
        UNSAFE_className={twMerge(editor.isActive("bold") && "bg-neutral-soft")}
      >
        <Bold />
      </Button>
      <Button
        icon
        variant="tertiary"
        size="sm"
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleItalic().run();
          });
        }}
        UNSAFE_className={twMerge(
          editor.isActive("italic") && "bg-neutral-soft",
        )}
      >
        <Italic />
      </Button>
      <Button
        icon
        variant="tertiary"
        size="sm"
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleUnderline().run();
          });
        }}
        UNSAFE_className={twMerge(
          editor.isActive("underline") && "bg-neutral-soft",
        )}
      >
        <UnderlineIcon />
      </Button>
      <Button
        icon
        variant="tertiary"
        size="sm"
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleStrike().run();
          });
        }}
        UNSAFE_className={twMerge(
          editor.isActive("strike") && "bg-neutral-soft",
        )}
      >
        <Strikethrough />
      </Button>
    </div>
  );

  const editorContent = (
    <div
      className="p-2 min-h-[80px] bg-white cursor-text"
      onClick={() => {
        if (!editor?.isFocused) {
          editor?.commands.focus("end");
        }
      }}
    >
      <EditorContent editor={editor} />
    </div>
  );

  return (
    <div className="content-modifier">
      <div className="border border-border rounded-lg overflow-hidden">
        {formattingToolbar}
        {editorContent}
      </div>
    </div>
  );
};

export default ContentModifierComponent;
